import { useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal that renders the exact email subscribers will receive for a post or book,
 * and lets the author send a real copy to their own inbox.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - type: "post" | "book"
 *  - payload: fields matching the create/update body (title, excerpt, cover_image, subtitle,
 *    description, buy_link, slug)
 */
export default function EmailPreviewModal({ open, onClose, type, payload }) {
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/preview", { type, send: false, ...payload });
      setHtml(data.html);
      setSubject(data.subject);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not render preview."));
    } finally { setLoading(false); }
  };

  // Load once when opening
  if (open && !html && !loading) load();
  if (!open) return null;

  const sendToMe = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/preview", { type, send: true, ...payload });
      toast.success(`Preview sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not send preview."));
    } finally { setSending(false); }
  };

  const close = () => { setHtml(""); setSubject(""); onClose(); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="preview-modal"
      onClick={close}
    >
      <div
        className="bg-[#FAFAFA] max-w-2xl w-full max-h-[92vh] flex flex-col border border-[#E5E5E5]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
          <div>
            <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#5C6670]">Email preview</div>
            <div className="font-serif text-lg text-[#1A1F24] truncate max-w-[520px]" data-testid="preview-subject">
              {subject || "…"}
            </div>
          </div>
          <button
            onClick={close}
            className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#1A1F24]"
            data-testid="preview-close"
            aria-label="Close preview"
          >
            Close ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-white" data-testid="preview-body">
          {loading || !html ? (
            <div className="p-16 text-center text-[#5C6670] italic">Rendering…</div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5] bg-white">
          <p className="text-xs text-[#5C6670] hidden sm:block">
            This is exactly what confirmed subscribers will receive.
          </p>
          <div className="flex gap-3 ml-auto">
            <button onClick={close} className="gv-btn-outline" data-testid="preview-cancel">Cancel</button>
            <button
              onClick={sendToMe}
              disabled={sending || loading}
              className="gv-btn-gold"
              data-testid="preview-send-me"
            >
              {sending ? "Sending…" : "Send preview to me"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
